<div class="channel-type-actions-dashboard">
  <!-- Header -->
  <div class="dashboard-header">
    <div class="header-left">
      <h1><i class="fa-solid fa-layer-group"></i> Channel Type Actions</h1>
      <span class="subtitle" *ngIf="selectedChannelType">{{ selectedChannelType.Name }}</span>
    </div>
    <div class="header-right">
      <!-- Organization Selector -->
      <div class="org-selector" *ngIf="organizations.length > 1">
        <label>Organization:</label>
        <select
          [ngModel]="selectedOrganization?.ID"
          (ngModelChange)="onOrganizationChange($event)"
        >
          <option *ngFor="let org of organizations" [value]="org.ID">{{ org.Name }}</option>
        </select>
      </div>
      <!-- Channel Type Selector -->
      <div class="channel-type-selector" *ngIf="filteredChannelTypes.length > 0">
        <label>Channel Type:</label>
        <select
          [ngModel]="selectedChannelType?.ID"
          (ngModelChange)="onChannelTypeChange($event)"
        >
          <option *ngFor="let ct of filteredChannelTypes" [value]="ct.ID">{{ ct.Name }}</option>
        </select>
      </div>
    </div>
  </div>

  <!-- Info Banner -->
  <div class="info-banner">
    <i class="fa-solid fa-info-circle"></i>
    <span>Channel Type Actions are inherited by all channels of this type. Configure defaults here, then override at the channel level if needed.</span>
  </div>

  <!-- Loading State -->
  <div class="loading-state" *ngIf="isLoading">
    <i class="fa-solid fa-spinner fa-spin"></i>
    <span>Loading channel type actions...</span>
  </div>

  <!-- Error Message -->
  <div class="error-banner" *ngIf="errorMessage">
    <i class="fa-solid fa-exclamation-triangle"></i>
    {{ errorMessage }}
    <button class="close-btn" (click)="errorMessage = ''">
      <i class="fa-solid fa-times"></i>
    </button>
  </div>

  <!-- Success Message -->
  <div class="success-banner" *ngIf="successMessage">
    <i class="fa-solid fa-check-circle"></i>
    {{ successMessage }}
  </div>

  <!-- Main Content -->
  <div class="dashboard-content" *ngIf="!isLoading && selectedChannelType && selectedOrganization">
    <!-- Toolbar -->
    <div class="toolbar">
      <div class="toolbar-left">
        <button class="btn btn-primary" (click)="startCreate()" [disabled]="isCreating || isSaving">
          <i class="fa-solid fa-plus"></i> Add Action
        </button>
      </div>
      <div class="toolbar-right">
        <div class="filter-group">
          <label>Status:</label>
          <select [(ngModel)]="statusFilter">
            <option *ngFor="let status of statusOptions" [value]="status">{{ status }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Create Form -->
    <div class="action-card create-card" *ngIf="isCreating">
      <div class="card-header">
        <h3><i class="fa-solid fa-plus-circle"></i> New Channel Type Action</h3>
      </div>
      <div class="card-body">
        <div class="form-row">
          <div class="form-group">
            <label>Action *</label>
            <select [(ngModel)]="createForm.actionId" required>
              <option value="">-- Select Action --</option>
              <option *ngFor="let action of availableActions" [value]="action.ID">{{ action.Name }}</option>
            </select>
          </div>
          <div class="form-group">
            <label>Status</label>
            <select [(ngModel)]="createForm.status">
              <option value="Active">Active</option>
              <option value="Pending">Pending</option>
              <option value="Revoked">Revoked</option>
            </select>
          </div>
          <div class="form-group">
            <label>Sequence</label>
            <input type="number" [(ngModel)]="createForm.sequence" min="0" />
          </div>
        </div>

        <div class="form-section">
          <h4><i class="fa-solid fa-key"></i> Default Credentials (Encrypted on Server)</h4>
          <p class="form-hint">These credentials will be used by all channels of this type unless overridden.</p>
          <div class="form-row">
            <div class="form-group">
              <label>API Key</label>
              <input type="password" [(ngModel)]="createForm.credentials.apiKey" placeholder="Enter API key" />
            </div>
            <div class="form-group">
              <label>Username</label>
              <input type="text" [(ngModel)]="createForm.credentials.username" placeholder="Username" />
            </div>
            <div class="form-group">
              <label>Password</label>
              <input type="password" [(ngModel)]="createForm.credentials.password" placeholder="Password" />
            </div>
          </div>
          <div class="form-row">
            <div class="form-group">
              <label>Client ID</label>
              <input type="text" [(ngModel)]="createForm.credentials.clientId" placeholder="OAuth Client ID" />
            </div>
            <div class="form-group">
              <label>Client Secret</label>
              <input type="password" [(ngModel)]="createForm.credentials.clientSecret" placeholder="OAuth Client Secret" />
            </div>
          </div>
        </div>

        <div class="form-group">
          <label>Credential Notes</label>
          <textarea [(ngModel)]="createForm.credentialNotes" rows="2" placeholder="Notes about these credentials"></textarea>
        </div>

        <div class="form-group">
          <label>Configuration (JSON)</label>
          <textarea [(ngModel)]="createForm.configuration" rows="4" placeholder='{"key": "value"}'></textarea>
        </div>
      </div>
      <div class="card-footer">
        <button class="btn btn-secondary" (click)="cancelCreate()" [disabled]="isSaving">Cancel</button>
        <button class="btn btn-primary" (click)="saveCreate()" [disabled]="isSaving || !createForm.actionId">
          <i class="fa-solid fa-save" *ngIf="!isSaving"></i>
          <i class="fa-solid fa-spinner fa-spin" *ngIf="isSaving"></i>
          Save
        </button>
      </div>
    </div>

    <!-- Actions List -->
    <div class="actions-list">
      <div class="empty-state" *ngIf="filteredActions.length === 0 && !isCreating">
        <i class="fa-solid fa-layer-group"></i>
        <p>No actions configured for this channel type</p>
        <button class="btn btn-primary" (click)="startCreate()">
          <i class="fa-solid fa-plus"></i> Add First Action
        </button>
      </div>

      <div
        class="action-card"
        *ngFor="let display of filteredActions; let i = index"
        [class.is-editing]="display.isEditing"
      >
        <!-- View Mode -->
        <ng-container *ngIf="!display.isEditing">
          <div class="card-header">
            <div class="action-info">
              <span class="sequence-badge">#{{ display.action.Sequence }}</span>
              <h3>{{ getActionName(display.action.IzzyActionID) }}</h3>
              <span class="status-badge" [ngClass]="getStatusClass(display.action.Status)">
                {{ display.action.Status }}
              </span>
              <span class="inheritance-badge">
                <i class="fa-solid fa-arrow-down"></i> Inherited by channels
              </span>
            </div>
            <div class="action-buttons">
              <button class="btn-icon" (click)="moveUp(display)" [disabled]="i === 0 || isSaving" title="Move Up">
                <i class="fa-solid fa-arrow-up"></i>
              </button>
              <button class="btn-icon" (click)="moveDown(display)" [disabled]="i === filteredActions.length - 1 || isSaving" title="Move Down">
                <i class="fa-solid fa-arrow-down"></i>
              </button>
              <button class="btn-icon" (click)="startEdit(display)" [disabled]="isSaving" title="Edit">
                <i class="fa-solid fa-edit"></i>
              </button>
              <button class="btn-icon btn-danger" (click)="confirmDelete(display)" [disabled]="isSaving" title="Delete">
                <i class="fa-solid fa-trash"></i>
              </button>
              <button class="btn-icon" (click)="openActionRecord(display)" title="Open Record">
                <i class="fa-solid fa-external-link-alt"></i>
              </button>
            </div>
          </div>
          <div class="card-body" *ngIf="display.action.CredentialNotes || display.action.Configuration || display.action.EncryptedCredentials">
            <div class="detail-row" *ngIf="display.action.CredentialNotes">
              <label>Credential Notes:</label>
              <span>{{ display.action.CredentialNotes }}</span>
            </div>
            <div class="detail-row" *ngIf="display.action.Configuration">
              <label>Configuration:</label>
              <code>{{ display.action.Configuration }}</code>
            </div>
            <div class="detail-row" *ngIf="display.action.EncryptedCredentials">
              <label>Credentials:</label>
              <span class="encrypted-indicator"><i class="fa-solid fa-lock"></i> Encrypted (default for channels)</span>
            </div>
          </div>
        </ng-container>

        <!-- Edit Mode -->
        <ng-container *ngIf="display.isEditing">
          <div class="card-header">
            <h3><i class="fa-solid fa-edit"></i> Edit Channel Type Action</h3>
          </div>
          <div class="card-body">
            <div class="form-row">
              <div class="form-group">
                <label>Action</label>
                <select [(ngModel)]="display.action.IzzyActionID" disabled>
                  <option *ngFor="let action of availableActions" [value]="action.ID">{{ action.Name }}</option>
                </select>
              </div>
              <div class="form-group">
                <label>Status</label>
                <select [(ngModel)]="display.editForm.status">
                  <option value="Active">Active</option>
                  <option value="Pending">Pending</option>
                  <option value="Revoked">Revoked</option>
                </select>
              </div>
              <div class="form-group">
                <label>Sequence</label>
                <input type="number" [(ngModel)]="display.editForm.sequence" min="0" />
              </div>
            </div>

            <div class="form-section">
              <h4><i class="fa-solid fa-key"></i> Update Default Credentials (leave blank to keep existing)</h4>
              <div class="form-row">
                <div class="form-group">
                  <label>API Key</label>
                  <input type="password" [(ngModel)]="display.editForm.credentials.apiKey" placeholder="Enter new API key" />
                </div>
                <div class="form-group">
                  <label>Username</label>
                  <input type="text" [(ngModel)]="display.editForm.credentials.username" placeholder="Username" />
                </div>
                <div class="form-group">
                  <label>Password</label>
                  <input type="password" [(ngModel)]="display.editForm.credentials.password" placeholder="Password" />
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label>Client ID</label>
                  <input type="text" [(ngModel)]="display.editForm.credentials.clientId" placeholder="OAuth Client ID" />
                </div>
                <div class="form-group">
                  <label>Client Secret</label>
                  <input type="password" [(ngModel)]="display.editForm.credentials.clientSecret" placeholder="OAuth Client Secret" />
                </div>
              </div>
            </div>

            <div class="form-group">
              <label>Credential Notes</label>
              <textarea [(ngModel)]="display.editForm.credentialNotes" rows="2"></textarea>
            </div>

            <div class="form-group">
              <label>Configuration (JSON)</label>
              <textarea [(ngModel)]="display.editForm.configuration" rows="4"></textarea>
            </div>
          </div>
          <div class="card-footer">
            <button class="btn btn-secondary" (click)="cancelEdit(display)" [disabled]="isSaving">Cancel</button>
            <button class="btn btn-primary" (click)="saveEdit(display)" [disabled]="isSaving">
              <i class="fa-solid fa-save" *ngIf="!isSaving"></i>
              <i class="fa-solid fa-spinner fa-spin" *ngIf="isSaving"></i>
              Save Changes
            </button>
          </div>
        </ng-container>
      </div>
    </div>
  </div>

  <!-- No Channel Type Selected -->
  <div class="empty-state" *ngIf="!isLoading && !selectedChannelType">
    <i class="fa-solid fa-layer-group"></i>
    <p>Select a channel type to manage its default actions</p>
  </div>

  <!-- Confirm Dialog -->
  <div class="confirm-overlay" *ngIf="confirmDialogVisible" (click)="closeConfirmDialog()">
    <div class="confirm-dialog" (click)="$event.stopPropagation()">
      <h3>{{ confirmDialogTitle }}</h3>
      <p>{{ confirmDialogMessage }}</p>
      <div class="dialog-buttons">
        <button class="btn btn-secondary" (click)="closeConfirmDialog()">Cancel</button>
        <button class="btn btn-danger" (click)="confirmDialogYes()">Delete</button>
      </div>
    </div>
  </div>
</div>
